import {
  BitkitAlert,
  BitkitButton,
  BitkitFormDialog,
  BitkitLink,
  BitkitNativeSelect,
  BitkitTextInput,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { useContext } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

import CountrySelector from '@/components/CountrySelector/CountrySelector';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import URLs from '@/enums/urls';
import useMonolithApiMutation from '@/hooks/api/useMonolithApiMutation';
import { SimpleErrorResponse } from '@/models/Error';
import { GlobalPropsWithAccount } from '@/models/GlobalProps';
import { getExtendLimitPath, planSelectorPagePath } from '@/utils/monolithApiRouteService';
import { assignLocation } from '@/utils/navigation';

const COMPANY_SIZE_OPTIONS = ['1-500', '501-750', '751-5000', '5001-10000', '10000+'];

type Inputs = {
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  companyName: string;
  companySize: string;
  country: string;
};

type ExtendYourLimitDialogProps = {
  isOpen: boolean;
  onClose?: () => void;
};

const ExtendYourLimitDialog = ({ isOpen, onClose }: ExtendYourLimitDialogProps) => {
  const {
    account: { slug },
  } = useContext(GlobalPropsContext) as GlobalPropsWithAccount;

  const { control, handleSubmit } = useForm<Inputs>({
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      companyName: '',
      companySize: '',
      country: '',
    },
  });

  const { mutate, isError, isPending, error } = useMonolithApiMutation<unknown, SimpleErrorResponse, unknown>(
    getExtendLimitPath(slug),
    'post',
    {
      onSuccess: () => {
        onClose?.();
        assignLocation(window.location.origin);
      },
    },
  );

  let errorMsg;
  switch (error?.response?.data?.errorMsg) {
    case 'not_on_time_based_trial': {
      errorMsg = (
        <>
          Your Workspace is not on a time based trial.{' '}
          <BitkitLink href={URLs.CONTACT_SUPPORT} textDecoration="underline" isExternal>
            Contact support
          </BitkitLink>
        </>
      );
      break;
    }
    case 'time_based_trial_limit_already_extended': {
      errorMsg = (
        <>
          Your Workspace already extended the credit limit for your trial.{' '}
          <BitkitLink href={planSelectorPagePath(slug)} textDecoration="underline" isExternal>
            Select a plan
          </BitkitLink>{' '}
          or{' '}
          <BitkitLink href={URLs.CONTACT_SALES} textDecoration="underline" isExternal>
            contact sales
          </BitkitLink>{' '}
          to continue using Bitrise.
        </>
      );
      break;
    }
    default: {
      errorMsg = (
        <>
          Submit and extend failed. Please try again. If the problem still persists,{' '}
          <BitkitLink href={URLs.CONTACT_SUPPORT} textDecoration="underline" isExternal>
            contact support
          </BitkitLink>
          .
        </>
      );
    }
  }

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    mutate(data);
  };

  return (
    <BitkitFormDialog
      open={isOpen}
      onOpenChange={({ open }) => !open && onClose?.()}
      state={isPending ? 'loading' : undefined}
      onSubmit={handleSubmit(onSubmit)}
      title="Extend your limit"
      footerContent={isError && <BitkitAlert variant="critical" messageText={errorMsg} />}
      footerButtons={
        <>
          <BitkitButton state={isPending ? 'disabled' : undefined} onClick={onClose} variant="secondary">
            Cancel
          </BitkitButton>
          <BitkitButton state={isPending ? 'loading' : undefined} type="submit">
            Submit & extend
          </BitkitButton>
        </>
      }
    >
      <Text>
        Your workspace usage has exceeded our fair usage limits. Submit this form to extend your limits and continue
        exploring Bitrise.
      </Text>
      <Box display="flex" gap="24">
        <Controller
          control={control}
          name="firstName"
          render={({ field }) => <BitkitTextInput flex="1" label="First name" inputProps={field} />}
        />
        <Controller
          control={control}
          name="lastName"
          render={({ field }) => <BitkitTextInput flex="1" label="Last name" inputProps={field} />}
        />
      </Box>
      <Controller
        control={control}
        name="phoneNumber"
        render={({ field }) => <BitkitTextInput label="Phone number" optional inputProps={field} />}
      />
      <Controller
        control={control}
        name="companyName"
        render={({ field }) => <BitkitTextInput label="Company name" inputProps={field} />}
      />
      <Controller
        control={control}
        name="companySize"
        render={({ field: { ref, ...fieldProps } }) => (
          <BitkitNativeSelect ref={ref} label="Company size" placeholder="Select an option" {...fieldProps}>
            {COMPANY_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </BitkitNativeSelect>
        )}
      />
      <Controller
        control={control}
        name="country"
        render={({ field: { ref, ...fieldProps } }) => <CountrySelector ref={ref} {...fieldProps} />}
      />
    </BitkitFormDialog>
  );
};

export default ExtendYourLimitDialog;
